import type {
  BasicConstraintsExtension,
  SubjectAlternativeNameExtension,
} from "@peculiar/x509";
import { useEffect, useState } from "react";
import {
  certificateFingerprints,
  certificatePemBlocks,
  trimmedCaCertificate,
} from "../../../shared/connectors/caCertificate";
import { parseX509Certificate } from "../../../shared/tunnel/cert";

const SUBJECT_ALTERNATIVE_NAME_OID = "2.5.29.17";
const BASIC_CONSTRAINTS_OID = "2.5.29.19";

export type CertificateDescription = {
  pem: string;
  subject: string;
  commonName: string;
  issuer: string;
  issuerCommonName: string;
  notBefore: number;
  notAfter: number;
  sha256: string;
  subjectAlternativeNames: string[];
  selfSigned: boolean;
  isCa: boolean;
};

async function describeBlock(pem: string): Promise<CertificateDescription> {
  const parsed = await parseX509Certificate(pem);
  const [sha256] = await certificateFingerprints(pem);
  return {
    pem,
    subject: parsed.subject,
    commonName: parsed.subjectName.getField("CN")[0] ?? parsed.subject,
    issuer: parsed.issuer,
    issuerCommonName: parsed.issuerName.getField("CN")[0] ?? parsed.issuer,
    notBefore: parsed.notBefore.getTime(),
    notAfter: parsed.notAfter.getTime(),
    sha256: sha256 ?? "",
    subjectAlternativeNames:
      parsed
        .getExtension<SubjectAlternativeNameExtension>(
          SUBJECT_ALTERNATIVE_NAME_OID
        )
        ?.names.items.map((name) => name.value) ?? [],
    selfSigned: parsed.subject === parsed.issuer,
    isCa:
      parsed.getExtension<BasicConstraintsExtension>(BASIC_CONSTRAINTS_OID)
        ?.ca === true,
  };
}

async function describeCertificates(
  value: string | undefined
): Promise<CertificateDescription[]> {
  const unwrapped = trimmedCaCertificate(value);
  const blocks = unwrapped ? certificatePemBlocks(unwrapped) : null;
  return Promise.all((blocks ?? []).map(describeBlock));
}

export function useCertificateDescriptions(
  value: string | undefined
): CertificateDescription[] | undefined {
  const [loaded, setLoaded] = useState<{
    value: string | undefined;
    descriptions: CertificateDescription[];
  }>();
  useEffect(() => {
    let cancelled = false;
    void describeCertificates(value).then((descriptions) => {
      if (!cancelled) setLoaded({ value, descriptions });
    });
    return () => {
      cancelled = true;
    };
  }, [value]);
  return loaded && loaded.value === value ? loaded.descriptions : undefined;
}

export function coversHost(
  subjectAlternativeNames: readonly string[],
  target: string
): boolean {
  const host = new URL(
    target.includes("://") ? target : `https://${target}`
  ).hostname
    .replace(/^\[|\]$/g, "")
    .toLowerCase();
  return subjectAlternativeNames.some((name) => {
    const pattern = name.toLowerCase();
    if (pattern === host) return true;
    const dot = host.indexOf(".");
    return (
      pattern.startsWith("*.") &&
      dot > 0 &&
      pattern.slice(1) === host.slice(dot)
    );
  });
}
